import React from 'react'
import isEmpty from 'lodash/isEmpty'
import { useToggle } from 'react-use'
import { useError, useMutation, useToast } from '@ztechapps/common.hook'
import { buildGraphqlQuery, getWindow } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { buildBlogPreviewUrl, mintBlogPreviewSlug } from '@ztechapps/blog.common'

const slugMutation = buildGraphqlQuery('Blog', '_id updatedAt slug').updateConfig

export type OpenPreviewButtonProps = {
  blogId: string
  slug: string
  disabled?: boolean
  onSlugSaved?: () => Promise<void> | void
}

/**
 * Opens the standalone preview for a blog post in a new tab, minting and
 * saving a slug first when the post has none — the opened tab then shows the
 * post it was opened from.
 */
export const OpenPreviewButton = ({ blogId, slug, disabled = false, onSlugSaved }: OpenPreviewButtonProps) => {
  const [onSaveSlug] = useMutation(slugMutation)
  const { showSuccess } = useToast()
  const { onError } = useError()
  const [opening, setOpening] = useToggle(false)

  const onOpen = async () => {
    setOpening(true)
    try {
      // The slug is the only credential on the preview URL, so a post saved
      // without one gets a minted slug saved first — the opened tab then shows
      // the post it was opened from.
      let resolvedSlug = slug ?? ''
      if (isEmpty(resolvedSlug)) {
        resolvedSlug = mintBlogPreviewSlug()
        const { errors } = await onSaveSlug({ input: { _id: blogId, slug: resolvedSlug } })
        if (errors) throw errors
        await onSlugSaved?.()
      }
      // New-tab shape follows the rank-1 open-in-new-tab precedent
      // (`apps/panelpunches/admin/src/view/productions/item/consumption/RowAction.tsx:107-114`),
      // routed through `getWindow()` per the WebUI guide's no-bare-window rule.
      getWindow().open(buildBlogPreviewUrl(getWindow().location.origin, resolvedSlug), '_blank', 'noopener,noreferrer')
      showSuccess({ message: 'Preview opened in a new tab.' })
    } catch (e) {
      onError(e)
    } finally {
      setOpening(false)
    }
  }

  return (
    <BusyButton
      text="Open preview"
      icon="arrow-up-right-from-square"
      isBusy={opening}
      disabled={disabled || !blogId}
      onClick={onOpen}
    />
  )
}
